import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Link from '@mui/material/Link';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { blogApi, blogKeys } from '../api/blog.api';

/** Medium-inspired reading column (~680px). */
const READ_MAX = 680;

export function BlogDetailPage() {
  const { slug = '' } = useParams();

  const { data: post, isLoading, isError } = useQuery({
    queryKey: blogKeys.detail(slug),
    queryFn: () => blogApi.getBySlug(slug),
    enabled: Boolean(slug),
  });

  const publishedLabel = post
    ? dayjs(post.publishedAt || post.createdAt).format('MMM D, YYYY')
    : '';
  const authorName = post?.author?.name?.trim() || 'trplin';
  const authorInitial = authorName.charAt(0).toUpperCase();

  return (
    <Box sx={{ bgcolor: '#FFFFFF', minHeight: '70vh', pb: { xs: 8, md: 12 } }}>
      <Container
        maxWidth={false}
        sx={{
          pt: { xs: 2.5, md: 4 },
          px: { xs: 2.5, sm: 3 },
          maxWidth: READ_MAX + 48,
          mx: 'auto',
        }}
      >
        <Link
          component={RouterLink}
          to="/blogs"
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            mb: { xs: 3, md: 4 },
            color: 'text.secondary',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          All blogs
        </Link>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ borderRadius: 1 }}>
            This blog post could not be found or is no longer published.
          </Alert>
        )}

        {post && (
          <Box component="article" sx={{ maxWidth: READ_MAX, mx: 'auto' }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
                fontWeight: 700,
                fontSize: { xs: '2.125rem', sm: '2.75rem', md: '3.25rem' },
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
                color: '#0F172A',
                mb: 2,
                textWrap: 'balance',
              }}
            >
              {post.title}
            </Typography>

            {post.excerpt && (
              <Typography
                sx={{
                  fontSize: { xs: '1.125rem', md: '1.25rem' },
                  lineHeight: 1.55,
                  color: '#64748B',
                  mb: 3,
                  fontWeight: 400,
                  textWrap: 'pretty',
                }}
              >
                {post.excerpt}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: { xs: 3.5, md: 4.5 },
              }}
            >
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: 'primary.main',
                  fontSize: '1rem',
                  fontWeight: 700,
                }}
              >
                {authorInitial}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#0F172A', lineHeight: 1.3 }}>
                  {authorName}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: '#64748B', mt: 0.25 }}>
                  {publishedLabel}
                </Typography>
              </Box>
            </Box>

            {post.coverImage && (
              <Box
                component="img"
                src={post.coverImage}
                alt=""
                sx={{
                  width: '100%',
                  maxHeight: { xs: 320, md: 420 },
                  objectFit: 'cover',
                  display: 'block',
                  mb: { xs: 4, md: 5 },
                  borderRadius: 1,
                }}
              />
            )}

            <Box
              sx={{
                textAlign: 'left',
                '& > *:first-of-type': { mt: 0 },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 1,
                  my: { xs: 3, md: 4 },
                  display: 'block',
                },
                '& h1, & h2, & h3': {
                  fontFamily: '"Playfair Display", Georgia, serif',
                  color: '#0F172A',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  mt: { xs: 4, md: 5 },
                  mb: 1.5,
                  lineHeight: 1.25,
                },
                /* Avoid repeating the page title if editor also put an h1 */
                '& h1': { fontSize: { xs: '1.5rem', md: '1.75rem' } },
                '& h2': { fontSize: { xs: '1.375rem', md: '1.625rem' } },
                '& h3': { fontSize: { xs: '1.2rem', md: '1.35rem' } },
                '& p': {
                  mb: 2.25,
                  lineHeight: 1.75,
                  fontSize: { xs: '1.125rem', md: '1.25rem' },
                  color: '#292929',
                  letterSpacing: '-0.003em',
                  fontFamily: 'Georgia, "Times New Roman", "Charter", serif',
                },
                '& a': {
                  color: 'primary.main',
                  textDecorationColor: 'rgba(37, 99, 235, 0.35)',
                  textUnderlineOffset: 3,
                },
                '& ul, & ol': {
                  pl: 3,
                  mb: 2.5,
                  lineHeight: 1.75,
                  fontSize: { xs: '1.125rem', md: '1.25rem' },
                  color: '#292929',
                  fontFamily: 'Georgia, "Times New Roman", "Charter", serif',
                },
                '& li': { mb: 1 },
                '& blockquote': {
                  m: 0,
                  my: 3,
                  pl: 2.5,
                  borderLeft: '3px solid',
                  borderColor: 'divider',
                  color: '#475569',
                  fontStyle: 'italic',
                  fontSize: { xs: '1.125rem', md: '1.25rem' },
                  lineHeight: 1.7,
                },
              }}
              dangerouslySetInnerHTML={{ __html: post.contentHtml }}
            />

            <Divider sx={{ my: { xs: 5, md: 6 }, borderColor: 'divider' }} />

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#0F172A', mb: 0.5 }}>
                  Enjoyed this story?
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                  Explore more guides and plan your next trip with trplin.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.25, flexShrink: 0 }}>
                <Button
                  component={RouterLink}
                  to="/blogs"
                  variant="outlined"
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1, px: 2 }}
                >
                  More stories
                </Button>
                <Button
                  component={RouterLink}
                  to="/guides"
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1,
                    px: 2,
                    boxShadow: 'none',
                  }}
                >
                  Find a guide
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}
